import { Component, signal } from '@angular/core';

import { of } from 'rxjs';

import {
  ActionButtonComponent,
  BadgeComponent,
  CamExpansionPanelComponent,
  CamPanelContentComponent,
  CamPanelTitleComponent,
  CardComponent,
  DualButtonComponent,
  ExpandableTextComponent,
} from '@camelot/ui';
import { CamPermissionsService, GuardComponent } from '@camelot/user';
import { CamClickOrDoubleClickDirective, StopPropagationDirective } from '@camelot/utils';

import { TestIdDirective } from '@camelot/testing-harness';

const deniedPermissionsMock = {
  canAccess$: () => of(false),
  canDirectAccess: () => false,
  isAuthenticated: false,
};

@Component({
    imports: [
        ActionButtonComponent,
        BadgeComponent,
        CamClickOrDoubleClickDirective,
        CamExpansionPanelComponent,
        CamPanelContentComponent,
        CamPanelTitleComponent,
        CardComponent,
        DualButtonComponent,
        ExpandableTextComponent,
        GuardComponent,
        StopPropagationDirective,
        TestIdDirective,
    ],
    providers: [{ provide: CamPermissionsService, useValue: deniedPermissionsMock }],
    template: `
    <div camTestId="mb-propagation-parent" (click)="this.parentClicks.set(this.parentClicks() + 1)">
      <button camTestId="mb-propagation-child" appStopPropagation (click)="this.childClicks.set(this.childClicks() + 1)">
        child
      </button>
    </div>
    <div camTestId="mb-propagation-counts">parent={{ this.parentClicks() }} child={{ this.childClicks() }}</div>

    <button
      camTestId="mb-click-dbl"
      camClickOrDoubleClick
      [clickHandler]="this.onSingle"
      [dblClickHandler]="this.onDouble"
    >
      click or double
    </button>
    <div camTestId="mb-click-dbl-counts">single={{ this.singleClicks() }} double={{ this.doubleClicks() }}</div>

    <cam-guard camTestId="mb-guard-denied" level="admin" feature="restricted">
      <div camTestId="mb-guard-secret">secret content</div>
    </cam-guard>

    <cam-dual-button camTestId="mb-dual-button" [first]="this.first" [second]="this.second" />
    <div camTestId="mb-dual-counts">first={{ this.firstClicks() }} second={{ this.secondClicks() }}</div>

    <cam-card camTestId="mb-card" (click)="this.cardClicks.set(this.cardClicks() + 1)">card content</cam-card>
    <div camTestId="mb-card-clicks">{{ this.cardClicks() }}</div>

    <cam-expandable-text camTestId="mb-expandable" [height]="40">
      <div>line 1</div>
      <div>line 2</div>
      <div>line 3</div>
      <div>line 4</div>
      <div>line 5</div>
      <div>line 6</div>
      <div>line 7</div>
      <div>line 8</div>
    </cam-expandable-text>

    <cam-action-button camTestId="mb-action-button" [actions]="this.actions" />
    <div camTestId="mb-action-clicks">{{ this.actionClicks() }}</div>

    <cam-badge camTestId="mb-badge" value="badge-value" [showClickOption]="true" (click)="this.badgeClicks.set(this.badgeClicks() + 1)" />
    <div camTestId="mb-badge-clicks">{{ this.badgeClicks() }}</div>

    <cam-expansion-panel camTestId="mb-expansion" toggleLabel="expansion toggle">
      <cam-panel-title>
        <div camTestId="mb-expansion-title">expansion title</div>
      </cam-panel-title>
      <cam-panel-content>
        <div camTestId="mb-expansion-content">expansion content</div>
      </cam-panel-content>
    </cam-expansion-panel>
  `
})
export class MiscBehaviorsCase {
  public actionClicks = signal(0);
  public badgeClicks = signal(0);
  public actions = [{ callback: () => this.actionClicks.update(count => count + 1), icon: 'add', label: 'Add' }];
  public cardClicks = signal(0);
  public childClicks = signal(0);
  public doubleClicks = signal(0);
  public first = { callback: () => this.firstClicks.update(count => count + 1), icon: 'list', label: 'First' };
  public firstClicks = signal(0);
  public parentClicks = signal(0);
  public second = { callback: () => this.secondClicks.update(count => count + 1), icon: 'map', label: 'Second' };
  public secondClicks = signal(0);
  public singleClicks = signal(0);

  public onDouble = (): void => this.doubleClicks.update(count => count + 1);
  public onSingle = (): void => this.singleClicks.update(count => count + 1);
}
